import React, { useState } from "react";
import { X, Loader2, ExternalLink } from "lucide-react";
import toast from "react-hot-toast";

import { createGarageSale, MANUAL_SOURCES } from "../../../api/garageSales";

const TEAL = "#55c5d0";

const Label = ({ children, required }) => (
  <label className="block text-[11px] font-extrabold uppercase tracking-wide text-gray-400 mb-1">
    {children}{required && <span className="text-red-500"> *</span>}
  </label>
);

const inputClass = "w-full px-3 py-2 rounded-lg border border-gray-300 text-sm";

/**
 * Key in a sale spotted on Gumtree or in the Facebook group.
 *
 * Street and suburb are the fields that matter: they are what the CRM matches
 * against the owners we already hold, so a sale saved without them is just a
 * note. Everything else is optional.
 */
const AddGarageSaleModal = ({ agents = [], onClose, onCreated }) => {
  const [form, setForm] = useState({
    source: "gumtree",
    title: "",
    description: "",
    street: "",
    suburb: "",
    postcode: "",
    sale_date: "",
    sale_time: "",
    url: "",
    assigned_agent: "",
    agent_notes: "",
  });
  const [saving, setSaving] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.street.trim() || !form.suburb.trim()) {
      toast.error("Street and suburb are needed so the sale can be matched to owners.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, state: "VIC" };
      if (!payload.assigned_agent) delete payload.assigned_agent;
      if (!payload.sale_date) delete payload.sale_date;
      const res = await createGarageSale(payload);
      const matches = res.data?.match_count || 0;
      toast.success(
        matches
          ? `Saved. ${matches} owner${matches === 1 ? "" : "s"} we know on that street.`
          : "Saved. No owner records on that street yet."
      );
      onCreated?.(res.data);
    } catch (err) {
      const data = err?.response?.data;
      const first = data && typeof data === "object" ? Object.values(data)[0] : null;
      toast.error(Array.isArray(first) ? first[0] : first || "Could not save that sale.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="font-extrabold text-gray-900">Add a garage sale</h2>
            <p className="text-[11px] text-gray-500">For sales you spotted on Gumtree or Facebook.</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100">
            <X size={18} className="text-gray-500" />
          </button>
        </div>

        <form onSubmit={submit} className="px-5 py-4">
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {MANUAL_SOURCES.map((s) => (
              <a key={s.key} href={s.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: TEAL }}>
                Open {s.key === "gumtree" ? "Gumtree" : "the Facebook group"} <ExternalLink size={11} />
              </a>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Where you saw it</Label>
              <select value={form.source} onChange={set("source")} className={inputClass}>
                <option value="gumtree">Gumtree</option>
                <option value="facebook">Facebook group</option>
                <option value="manual">Somewhere else</option>
              </select>
            </div>

            <div className="col-span-2">
              <Label>Title</Label>
              <input value={form.title} onChange={set("title")} className={inputClass}
                     placeholder="e.g. Downsizing sale, 30 years in the house" />
            </div>

            <div className="col-span-2">
              <Label>What the ad says</Label>
              <textarea value={form.description} onChange={set("description")} rows={3}
                        className={inputClass}
                        placeholder="Paste the wording. It is what decides whether this reads as a mover." />
            </div>

            <div>
              <Label required>Street</Label>
              <input value={form.street} onChange={set("street")} className={inputClass}
                     placeholder="e.g. Otterington Grove" />
            </div>
            <div>
              <Label required>Suburb</Label>
              <input value={form.suburb} onChange={set("suburb")} className={inputClass}
                     placeholder="e.g. Ivanhoe East" />
            </div>

            <div>
              <Label>Postcode</Label>
              <input value={form.postcode} onChange={set("postcode")} className={inputClass} />
            </div>
            <div>
              <Label>Date</Label>
              <input type="date" value={form.sale_date} onChange={set("sale_date")} className={inputClass} />
            </div>

            <div>
              <Label>Time</Label>
              <input value={form.sale_time} onChange={set("sale_time")} className={inputClass}
                     placeholder="9:00am - 2:00pm" />
            </div>
            <div>
              <Label>Assign to</Label>
              <select value={form.assigned_agent} onChange={set("assigned_agent")} className={inputClass}>
                <option value="">Unassigned</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name || a.email}</option>
                ))}
              </select>
            </div>

            <div className="col-span-2">
              <Label>Link to the ad</Label>
              <input value={form.url} onChange={set("url")} className={inputClass}
                     placeholder="https://" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-5">
            <button type="button" onClick={onClose}
                    className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-bold text-gray-600">
              Cancel
            </button>
            <button type="submit" disabled={saving}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-white text-sm font-bold disabled:opacity-60"
                    style={{ background: TEAL }}>
              {saving && <Loader2 size={15} className="animate-spin" />}
              Save sale
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddGarageSaleModal;
